import { MortgageAnnotations } from '@cian/adfox-templates';
import { useBooleanState } from '@cian/react-utils';
import { Button, IconButton, Image, Label, UIHeading3, UIText1, UIText2, UIText3 } from '@cian/ui-kit';
import { IconActionClose16, IconProductDocumentsVerified16 } from '@cian/ui-kit-design-tokens/icons';
import { clsx } from 'clsx';
import * as React from 'react';

import * as styles from './TgbNativedescGeneral.css';
import { ITransportProps, Transport } from './components/Transport';

interface ITransportIterable extends ITransportProps {
  id: number;
}

export interface ITgbNativedescGeneralProps {
  sizes: { width: string; height: number };
  url: string;
  image: string;
  logoImageUrl: string | undefined;
  title: string;
  description: string;
  address: string;
  builderName: string;
  annotation: string;
  button: string | undefined;
  label: string | undefined;
  transports: ITransportIterable[];
  mortgageWarning: string | undefined;
  mortgageTermsWarning: string | undefined;
}

export const TgbNativedescGeneral: React.FC<ITgbNativedescGeneralProps> = props => {
  const {
    title,
    address,
    description,
    annotation,
    image,
    transports,
    url,
    button,
    label,
    builderName,
    sizes,
    mortgageWarning,
    mortgageTermsWarning,
  } = props;

  const { state: isClosed, setTrue: setClosed } = useBooleanState();

  const handleClose = React.useCallback<(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void>(
    event => {
      event.preventDefault();

      setClosed();
    },
    [setClosed],
  );

  if (isClosed) {
    return <div className={styles['empty']} data-testid="empty_native_adfox_block" />;
  }

  const withMortgageAnnotations = !!(mortgageWarning || mortgageTermsWarning);

  return (
    <a
      className={clsx(styles['container'], withMortgageAnnotations && styles['container--mortgage-annotations'])}
      href={url}
      rel="noopener noreferrer"
      style={{ width: sizes.width }}
      target="_blank"
    >
      <div className={styles['snippet']}>
        <div className={styles['photo']}>
          <Image height="100%" objectFit="cover" src={image} width="100%" />

          {!!label && (
            <div className={styles['badge']}>
              <Label background="control-main-secondary-default" color="theme-dark">
                {label}
              </Label>
            </div>
          )}

          {!!annotation && (
            <div className={styles['disclaimer']}>
              <UIText3 color="text-secondary-default">{annotation}</UIText3>
            </div>
          )}
        </div>

        <div className={styles['content']}>
          <div className={styles['top']}>
            <div className={styles['company']}>
              <UIText3 color="text-secondary-default" data-testid="builderName">
                Компания {builderName}
              </UIText3>
              <IconProductDocumentsVerified16 color="accent-main-primary" />
            </div>

            <UIHeading3 as="h3" color="accent-main-primary">
              {title}
            </UIHeading3>

            <div className={styles['description']}>
              <UIText1 fontWeight="bold">{description}</UIText1>
            </div>

            <div className={styles['geo']}>
              {!!transports.length && (
                <div className={styles['geo-row']}>
                  {transports.map(transport => (
                    <Transport
                      iconColor={transport.iconColor}
                      iconType={transport.iconType}
                      key={transport.id}
                      name={transport.name}
                      remoteness={transport.remoteness}
                    />
                  ))}
                </div>
              )}

              <div className={styles['address']}>
                <UIText2 color="text-secondary-default" data-testid="address">
                  {address}
                </UIText2>
              </div>
            </div>
          </div>

          <Button fullWidth data-testid="more-info-button" size="M">
            {button || 'Узнать подробности'}
          </Button>
        </div>
      </div>

      <div className={styles['close-block']} data-testid="close-block">
        <IconButton data-testid="close-button" icon={<IconActionClose16 />} onClick={handleClose} />
      </div>

      {withMortgageAnnotations && (
        <MortgageAnnotations
          height={sizes.height}
          mortgageTermsWarning={mortgageTermsWarning}
          mortgageWarning={mortgageWarning}
        />
      )}
    </a>
  );
};

TgbNativedescGeneral.displayName = 'TgbNativedescGeneral';
